
import React, {useState, useContext, useMemo} from "react";
import {
    Form,
    Input,
    InputNumber,
    Select,
    Button,
    Upload,
    Row,
    Col,
    Card,
    Spin,
    Alert,
    Popconfirm,
    Radio,
} from "antd";
import {DeleteOutlined, PlusOutlined} from "@ant-design/icons";
import {internalApi} from "../../common/axios";
import {getBannerImageUrl} from "../../common/S3";
import {DIALOG_KEY, onOffList, actionList} from "../../shared/utils/enum";
import {AuthContext} from "../../shared/contexts/authContext";
import toast from "react-hot-toast";
import CustomDateTimePicker from "../../shared/components/date/CustomDateTimePicker";
import {buildUrl} from "../../shared/utils/net";
import {compareVersions} from "../../shared/utils/basic";
import {BannerEntity} from "./BannerEntity";
import {useCountryData} from "../country/useCountryData";
import {useServerInfo} from "../serverInfo/useServerInfo";

const {Option} = Select;

const BannerEditModal = ({record, handleClose, dialogRef, handleSetDialog}) => {
    const {currentUser} = useContext(AuthContext);
    if (isNaN(record.target)) record.target = 1;
    const data = new BannerEntity(record);
    const [rows, setRows] = useState(data);
    const [images, setImages] = useState({});
    const [previews, setPreviews] = useState({});
    const [banners, setBanners] = useState(data.banners || []);
    const [saving, setSaving] = useState(false);
    const [versionMode, setVersionMode] = useState(() => {
        if (!data.isTarget) return "all";
        return data.targetVersionList?.length >= 2 ? "range" : "target";
    });
    const [rangeBounds, setRangeBounds] = useState(() => {
        if (data.isTarget && data.targetVersionList?.length >= 2) {
            const sorted = [...data.targetVersionList].sort(compareVersions);
            return [sorted[0], sorted[sorted.length - 1]];
        }
        return [undefined, undefined];
    });

    const [levelMode, setLevelMode] = useState(data.minLevel > 0 || data.maxLevel > 0 ? "range" : "all");

    const {data: countryData, isLoading, error} = useCountryData();
    const {data: serverInfoData} = useServerInfo();

    const targetValue = [
        {key: 1, text: "해당 국가만"},
        {key: 0, text: "모두 (US 기본)"},
    ];

    const versionOptions = useMemo(() => {
        const versions = serverInfoData?.serverInfo?.versions;
        if (!versions) return [];
        const all = [...(versions.aos || []), ...(versions.ios || [])].map((v) => v.version).filter(Boolean);
        return [...new Set(all)].sort(compareVersions);
    }, [serverInfoData]);

    const handleImagesChange = (countryCode, key, file) => {
        if (file) {
            setImages((prevImages) => ({
                ...prevImages,
                [`${key}_${countryCode}`]: {
                    file: file,
                    name: file.name,
                },
            }));
            setPreviews((prevPreviews) => ({
                ...prevPreviews,
                [`${key}_${countryCode}`]: URL.createObjectURL(file),
            }));
            handleSetBanners(countryCode, key, file.name);
        }
    };

    const handleSetRows = (key, value) => {
        setRows((prevState) => {
            const updatedData = {...prevState, [key]: value};
            return new BannerEntity(updatedData);
        });
        if (key === "target" && value === 0) {
            handleAddBanner("US");
        }
    };

    const handleVersionModeChange = (mode) => {
        setVersionMode(mode);
        if (mode === "all") {
            handleSetRows("isTarget", 0);
            handleSetRows("targetVersionList", []);
            setRangeBounds([undefined, undefined]);
        } else if (mode === "target") {
            handleSetRows("isTarget", 1);
            handleSetRows("targetVersionList", rows.targetVersionList.length === 1 ? rows.targetVersionList : []);
        } else if (mode === "range") {
            handleSetRows("isTarget", 1);
            handleSetRows("targetVersionList", applyRangeBounds(rangeBounds[0], rangeBounds[1]));
        }
    };

    const handleLevelModeChange = (mode) => {
        setLevelMode(mode);
        if (mode === "all") {
            handleSetRows("minLevel", 0);
            handleSetRows("maxLevel", 0);
        }
    };

    const handleSetTargetVersionList = (index, value) => {
        const newList = [...rows.targetVersionList];
        newList[index] = value;
        handleSetRows("targetVersionList", newList);
    };

    const applyRangeBounds = (from, to) => {
        if (!from || !to) return [];
        const [lower, upper] = [from, to].sort(compareVersions);
        return versionOptions.filter((v) => compareVersions(v, lower) >= 0 && compareVersions(v, upper) <= 0);
    };

    const handleSetRangeBound = (index, value) => {
        setRangeBounds((prev) => {
            const next = [...prev];
            next[index] = value;
            handleSetRows("targetVersionList", applyRangeBounds(next[0], next[1]));
            return next;
        });
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === "DELETE") {
            await internalApi
                .delete(buildUrl("banner", {bannerId: rows.id}))
                .then((res) => {
                    toast.success(res.data.message);
                    handleClose(true);
                })
                .catch((err) => {
                    toast.error("삭제 실패");
                    console.error(err);
                });
            handleSetDialog([
                {key: DIALOG_KEY.OPEN, value: false},
                {key: DIALOG_KEY.CONFIRM_TEXT, value: ""},
            ]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
        }
    };

    const handleSave = async () => {
        if (levelMode === "range" && (!rows.minLevel || !rows.maxLevel)) {
            toast.error("레벨 범위를 모두 입력하세요.");
            return;
        }
        if (saving || rows.isInvalidate()) return;
        setSaving(true);
        try {
            if (!(await uploadBanner())) return;
            await internalApi
                .post("banner", {id: rows.id, data: rows.toJson()})
                .then((res) => {
                    toast.success(res.data.message);
                    handleClose(true);
                })
                .catch((err) => {
                    console.error(err);
                    toast.error("저장 실패");
                });
            handleSetDialog([
                {key: DIALOG_KEY.OPEN, value: false},
                {key: DIALOG_KEY.CONFIRM_TEXT, value: ""},
            ]);
        } finally {
            setSaving(false);
        }
    };

    const handleSetBanners = (countryCode, key, value) => {
        setBanners((prevBanners) => {
            const newBanners = [...prevBanners];
            newBanners[newBanners.findIndex((banner) => banner.countryCode === countryCode)][key] = value;
            handleSetRows("banners", newBanners);
            return newBanners;
        });
    };

    const handleAddBanner = (countryCode) => {
        if (banners.some((banner) => banner.countryCode === countryCode)) {
            return;
        }
        setBanners((prevBanners) => {
            const newBanners = [...prevBanners];
            newBanners.push({countryCode: countryCode, img: "", link: ""});
            handleSetRows("banners", newBanners);
            return newBanners;
        });
    };

    const handleRemoveBanner = (index) => {
        setBanners((prevBanners) => {
            const newBanners = [...prevBanners];
            newBanners.splice(index, 1);
            handleSetRows("banners", newBanners);
            return newBanners;
        });
    };

    const uploadBanner = async () => {
        for (const image of Object.values(images)) {
            if (!image) {
                toast.error("이미지를 등록하세요.");
                return false;
            }

            const formData = new FormData();
            formData.append('file', image.file);
            const { message } = await internalApi.post('upload/banner', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            // try {
            //     await uploadToS3(currentUser.target, key, image.file);
            // } catch (e) {
            //     toast.error("이미지 저장 실패");
            //     return false;
            // }
        }
        return true;
    };

    if (isLoading) return <Spin/>;
    if (error) return <Alert message="에러 발생" description={error.message} type="error" showIcon/>;

    const countryCodes = [...new Set(countryData?.map((row) => row.countryCode) ?? [])];
    const levelInvalid = rows.isLevelRangeInvalid();

    return (
        <Form layout="vertical" onFinish={handleSave} className="bannerCreate">
            <Row gutter={20}>
                <Col span={6}>
                    <Form.Item label="배너 이름" required>
                        <Input
                            value={rows.name}
                            onChange={(e) => handleSetRows("name", e.target.value)}
                            placeholder="배너 이름"
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="타겟" required>
                        <Select
                            value={rows.target}
                            onChange={v => handleSetRows('target', v)}
                            style={{width: '100%'}}
                        >
                            {targetValue.map((el) => (
                                <Option key={el.key} value={el.key}>{el.text}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="ON/OFF 설정" required>
                        <Select
                            value={rows.onOff}
                            onChange={(v) => handleSetRows("onOff", v)}
                            options={onOffList.map((option) => ({label: option.label, value: option.value}))}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="강제 노출 설정" required>
                        <Select
                            value={rows.isForcedExposure}
                            onChange={(v) => handleSetRows("isForcedExposure", v)}
                            options={onOffList.map((option) => ({label: option.label, value: option.value}))}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={20}>
                <Col span={24}>
                    <Form.Item label="버전" required>
                        <Radio.Group
                            value={versionMode}
                            onChange={(e) => handleVersionModeChange(e.target.value)}
                        >
                            <Radio value="all">All</Radio>
                            <Radio value="target">Target</Radio>
                            <Radio value="range">Range</Radio>
                        </Radio.Group>
                        {versionMode === "target" && (
                            <div style={{marginTop: 8}}>
                                <Select
                                    placeholder="Version"
                                    value={rows.targetVersionList[0]}
                                    onChange={(v) => handleSetTargetVersionList(0, v)}
                                    style={{width: 160}}
                                    showSearch
                                >
                                    {versionOptions.map((v) => (
                                        <Option key={v} value={v}>{v}</Option>
                                    ))}
                                </Select>
                            </div>
                        )}
                        {versionMode === "range" && (
                            <>
                                <div style={{marginTop: 8, display: "flex", alignItems: "center", gap: 8}}>
                                    <Select
                                        placeholder="Version"
                                        value={rangeBounds[0]}
                                        onChange={(v) => handleSetRangeBound(0, v)}
                                        style={{width: 160}}
                                        showSearch
                                    >
                                        {versionOptions.map((v) => (
                                            <Option key={v} value={v}>{v}</Option>
                                        ))}
                                    </Select>
                                    <span>~</span>
                                    <Select
                                        placeholder="Version"
                                        value={rangeBounds[1]}
                                        onChange={(v) => handleSetRangeBound(1, v)}
                                        style={{width: 160}}
                                        showSearch
                                    >
                                        {versionOptions.map((v) => (
                                            <Option key={v} value={v}>{v}</Option>
                                        ))}
                                    </Select>
                                </div>
                                {rows.targetVersionList.length > 0 && (
                                    <div style={{marginTop: 4, color: "#888"}}>
                                        포함된 버전: {rows.targetVersionList.join(", ")}
                                    </div>
                                )}
                            </>
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={20}>
                <Col span={24}>
                    <Form.Item
                        label="레벨"
                        required
                        validateStatus={levelInvalid ? "error" : undefined}
                        help={levelInvalid ? "최소 레벨이 최대 레벨보다 큽니다." : undefined}
                    >
                        <Radio.Group
                            value={levelMode}
                            onChange={(e) => handleLevelModeChange(e.target.value)}
                        >
                            <Radio value="all">All</Radio>
                            <Radio value="range">Range</Radio>
                        </Radio.Group>
                        {levelMode === "range" && (
                            <div style={{marginTop: 8, display: "flex", alignItems: "center", gap: 8}}>
                                <InputNumber
                                    placeholder="이상"
                                    value={rows.minLevel || null}
                                    min={1}
                                    precision={0}
                                    onChange={(v) => handleSetRows("minLevel", v ?? 0)}
                                    status={levelInvalid ? "error" : undefined}
                                    style={{width: 160}}
                                />
                                <span>~</span>
                                <InputNumber
                                    placeholder="이하"
                                    value={rows.maxLevel || null}
                                    min={1}
                                    precision={0}
                                    onChange={(v) => handleSetRows("maxLevel", v ?? 0)}
                                    status={levelInvalid ? "error" : undefined}
                                    style={{width: 160}}
                                />
                            </div>
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={20}>
                <Col span={8}>
                    <Form.Item label="링크 처리 방식" required>
                        <Select
                            value={rows.action}
                            onChange={(v) => handleSetRows("action", v)}
                            options={actionList.map((option) => ({label: option.label, value: option.value}))}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Priority">
                        <InputNumber
                            value={rows.priority}
                            min={0}
                            onChange={(v) => handleSetRows("priority", v)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </Col>

            </Row>
            <Row gutter={20}>
                <Col span={32}>
                    <Form.Item label="노출 기간">
                        <CustomDateTimePicker rows={rows} setRows={setRows} handleSetRows={handleSetRows}/>
                    </Form.Item>
                </Col>
            </Row>
            <div style={{marginBottom: 16}}>
                <Select
                    placeholder="배너 추가"
                    style={{width: 200}}
                    onChange={handleAddBanner}
                    value={undefined}
                >
                    {countryCodes.map((el) => (
                        <Option
                            key={el}
                            value={el}
                            disabled={banners.some((banner) => banner.countryCode === el)}
                        >
                            {el}
                        </Option>
                    ))}
                </Select>
            </div>
            <Row gutter={[16, 16]}>
                {banners.map((banner, index) => (
                    <Col span={24} key={index}>
                        <Card
                            size="small"
                            title={banner.countryCode}
                            extra={
                                <Popconfirm
                                    title="정말 삭제하시겠습니까?"
                                    onConfirm={() => handleRemoveBanner(index)}
                                    okText="삭제"
                                    cancelText="취소"
                                >
                                    <Button danger icon={<DeleteOutlined/>} size="small"/>
                                </Popconfirm>
                            }
                            style={{marginBottom: 8}}
                        >
                            <Row gutter={8}>
                                <Col span={24}>
                                    <div style={{marginBottom: 8}}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Upload
                                                accept="image/*"
                                                showUploadList={false}
                                                beforeUpload={(file) => {
                                                    handleImagesChange(banner.countryCode, "img", file);
                                                    return false;
                                                }}
                                            >
                                                <Button icon={<PlusOutlined/>}>이미지 업로드</Button>
                                            </Upload>
                                            <Input
                                                value={banner.link}
                                                onChange={(e) => handleSetBanners(banner.countryCode, "link", e.target.value)}
                                                placeholder={`${banner.countryCode}_Link`}
                                                style={{marginLeft: 10}}
                                            />
                                        </div>
                                        {(previews[`img_${banner.countryCode}`] || banner.img) && (
                                            <div className="hello-kitty-image" style={{marginTop: 8}}>
                                                <img
                                                    src={
                                                        previews[`img_${banner.countryCode}`] ||
                                                        getBannerImageUrl(currentUser.target, banner.img)
                                                    }
                                                    alt="파일 없음"
                                                    style={{width: "100%", height: 50, objectFit: "contain"}}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                ))}
            </Row>
            <Row justify="end" gutter={8} style={{marginTop: 24}}>
                <Col>
                    <Button onClick={() => handleClose(false)}>취소</Button>
                </Col>
                <Col>
                    <Popconfirm
                        title="정말 삭제하시겠습니까? 삭제하려면 'DELETE'를 입력하세요."
                        onConfirm={handleDelete}
                        okText="삭제"
                        cancelText="취소"
                        icon={<DeleteOutlined style={{color: 'red'}}/>}
                        description={<Input
                            onChange={e => handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: e.target.value}])}
                            placeholder="DELETE"/>}
                    >
                        <Button danger>삭제</Button>
                    </Popconfirm>
                </Col>
                <Col>
                    <Button type="primary" htmlType="submit" loading={saving}>저장</Button>
                </Col>
            </Row>
        </Form>
    );
};

export default BannerEditModal;
